import React, { useState, useEffect, useMemo } from 'react';
import {
  ReceiptPercentIcon,
  ArrowPathIcon,
  EyeIcon,
  FunnelIcon,
  ClockIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
  XMarkIcon,
  BuildingOffice2Icon,
  ChevronUpDownIcon,
  BoltIcon,
  ArrowTrendingUpIcon,
  CpuChipIcon,
  ChartBarIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import LoadingScreen from '../components/common/LoadingScreen';
import CommonPageHeader from '../components/common/CommonPageHeader';
import ClearAdminDownloader from '../components/common/ClearAdminDownloader';
import EmailTestModeToggle from '../components/common/EmailTestModeToggle';
import { energiaService, EnergiaBill } from '../services/energiaService';
import { useLanguage } from '../contexts/LanguageContext';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import { Combobox } from '@headlessui/react';


// Interface for consolidated bills
interface ConsolidatedBill {
  customer_id: string;
  customer_name: string;
  customer_info?: EnergiaBill['customer_info'];
  pod_count: number;
  pod_ids: string[];
  billing_period: string;
  total_consumption_kwh: number;
  total_cost_huf: number;
  average_price_huf_kwh: number;
  provider: string;
  bills: EnergiaBill[];
}

const EnergiaBilling = () => {
  const { t } = useLanguage();
  const [bills, setBills] = useState<EnergiaBill[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<string>('pending');
  const [selectedBill, setSelectedBill] = useState<EnergiaBill | null>(null);
  const [selectedConsolidatedBill, setSelectedConsolidatedBill] = useState<ConsolidatedBill | null>(null);
  const [viewMode, setViewMode] = useState<'individual' | 'consolidated'>('individual');
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [selectedCustomerType, setSelectedCustomerType] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [customerQuery, setCustomerQuery] = useState<string>('');

  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof EnergiaBill>('total_cost_huf');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Modal state for analytics
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(25);

  const statusOptions = [
    { value: 'pending', label: 'Várakozó', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'processed', label: 'Feldolgozott', color: 'bg-blue-100 text-blue-800' },
    { value: 'sent', label: 'Elküldött', color: 'bg-green-100 text-green-800' },
    { value: 'paid', label: 'Fizetve', color: 'bg-gray-100 text-gray-800' }
  ];

  useEffect(() => {
    loadBills();
  }, [selectedStatus]);

  const loadBills = async () => {
    try {
      setLoading(true);
      const data = await energiaService.getBillingQueue(selectedStatus);
      setBills(data);
    } catch (error) {
      console.error('Error loading bills:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to determine customer type based on customer_name
  const getCustomerType = (bill: EnergiaBill) => {
    const customerName = bill.customer_name || '';
    const customerInfo = bill.customer_info;
    
    // Ha van customer_info és érvényes típussal
    if (customerInfo && customerInfo.customer_type && 
        (customerInfo.customer_type === 'organization' || customerInfo.customer_type === 'individual')) {
      return customerInfo.customer_type;
    }
    
    // Heurisztika alapján próbáljuk kitalálni a customer_name-ből
    const lowerName = customerName.toLowerCase();
    
    // Cégek jellemző kifejezései
    if (lowerName.includes('kft') || lowerName.includes('bt') || lowerName.includes('zrt') || 
        lowerName.includes('kht') || lowerName.includes('nyrt') || lowerName.includes('rt') ||
        lowerName.includes('ltd') || lowerName.includes('inc') || lowerName.includes('corp') ||
        lowerName.includes('gmbh') || lowerName.includes('sa') || lowerName.includes('srl') ||
        lowerName.includes('spa') || lowerName.includes('ag') || lowerName.includes('ltd') ||
        lowerName.includes(' kft.') || lowerName.includes(' bt.') || lowerName.includes(' zrt.') ||
        lowerName.includes('nonprofit') || lowerName.includes('non-profit') || 
        lowerName.includes('foundation') || lowerName.includes('alapítvány') ||
        lowerName.includes('egyesület') || lowerName.includes('szövetség') ||
        lowerName.includes('kamara') || lowerName.includes('társaság') ||
        lowerName.includes('szolgáltatás') || lowerName.includes('kereskedelem') ||
        lowerName.includes('építés') || lowerName.includes('gyártás') ||
        lowerName.includes('bank') || lowerName.includes('biztosító') ||
        lowerName.includes('hotel') || lowerName.includes('étterem') ||
        lowerName.includes('iroda') || lowerName.includes('centrum') ||
        lowerName.includes('plaza') || lowerName.includes('bevásárló') ||
        lowerName.includes('iskola') || lowerName.includes('egyetem') ||
        lowerName.includes('kórház') || lowerName.includes('klinika') ||
        lowerName.includes('műhely') || lowerName.includes('gyár')) {
      return 'organization';
    }
    
    // Ha tartalmaz magyar keresztnevet, valószínűleg magánszemély
    const hungarianNames = ['andrás', 'péter', 'józsef', 'lászló', 'zoltán', 'gábor', 'attila', 'tibor', 
                           'györgy', 'ferenc', 'mihály', 'istván', 'tamás', 'zsolt', 'bálint', 'dániel',
                           'mária', 'erzsébet', 'katalin', 'ilona', 'judit', 'andrea', 'eva', 'edit',
                           'ágnes', 'anita', 'barbara', 'zsuzsanna', 'rita', 'mónika'];
    
    for (const name of hungarianNames) {
      if (lowerName.includes(name)) {
        return 'individual';
      }
    }
    
    // Default: no CRM connection
    return 'no_crm';
  };

  // Filter logic
  const filteredBills = bills.filter(bill => {
    // Search query filter
    if (searchQuery && !bill.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !bill.pod_id.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    // Customer filter - check both customer_name and customer_info.customer_name
    if (selectedCustomer) {
      const customerInfoName = bill.customer_info?.customer_name;
      if (bill.customer_name !== selectedCustomer && customerInfoName !== selectedCustomer) {
        return false;
      }
    }
    
    // Provider filter
    if (selectedProvider && bill.provider !== selectedProvider) {
      return false;
    }
    
    // Customer type filter
    if (selectedCustomerType) {
      const customerType = getCustomerType(bill);
      if (customerType !== selectedCustomerType) {
        return false;
      }
    }
    
    // Month filter
    if (selectedMonth && bill.billing_period !== selectedMonth) {
      return false;
    }
    
    return true;
  });

  const totalAmount = filteredBills.reduce((sum, bill) => sum + bill.total_cost_huf, 0);
  const totalConsumption = filteredBills.reduce((sum, bill) => sum + bill.total_consumption_kwh, 0);
  
  // Statistics számítások
  const companiesCount = filteredBills.filter(bill => getCustomerType(bill) === 'organization').length;
  const individualsCount = filteredBills.filter(bill => getCustomerType(bill) === 'individual').length;
  const noCrmCount = filteredBills.filter(bill => getCustomerType(bill) === 'no_crm').length;
  
  // CRM companies with customer_info
  const crmCompaniesCount = filteredBills.filter(bill => 
    bill.customer_info && 
    bill.customer_info.customer_type === 'organization' &&
    bill.customer_info.customer_name &&
    bill.customer_info.customer_name !== "Nincs hozzárendelve"
  ).length;
  
  // Get unique values for filters
  const uniqueProviders = Array.from(new Set(bills.map(bill => bill.provider))).sort();
  const uniqueMonths = Array.from(new Set(bills.map(bill => bill.billing_period))).sort().reverse();
  // Only show CRM companies (organizations with customer_info) in customer dropdown
  const uniqueCustomers = Array.from(new Set(
    bills
      .filter(bill => 
        bill.customer_info && 
        bill.customer_info.customer_type === 'organization' &&
        bill.customer_info.customer_name &&
        bill.customer_info.customer_name !== "Nincs hozzárendelve"
      )
      .map(bill => bill.customer_info!.customer_name!)
  )).sort();

  // Filtered customers for Combobox
  const filteredCustomers = uniqueCustomers.filter(customer =>
    customer.toLowerCase().includes(customerQuery.toLowerCase())
  );
  
  // Generate consolidated bills - group PODs by customer
  const consolidatedBills = useMemo(() => {
    const customerGroups: { [key: string]: EnergiaBill[] } = {};
    
    // Group bills by customer (using customer_info.customer_id if available, otherwise customer_name)
    filteredBills.forEach(bill => {
      const customerId = bill.customer_info?.customer_id || bill.customer_name;
      if (!customerGroups[customerId]) {
        customerGroups[customerId] = [];
      }
      customerGroups[customerId].push(bill);
    });
    
    // Convert groups to consolidated bills
    return Object.entries(customerGroups).map(([customerId, customerBills]): ConsolidatedBill => {
      const firstBill = customerBills[0];
      const totalConsumption = customerBills.reduce((sum, bill) => sum + bill.total_consumption_kwh, 0);
      const totalCost = customerBills.reduce((sum, bill) => sum + bill.total_cost_huf, 0);
      const averagePrice = totalConsumption > 0 ? totalCost / totalConsumption : 0;
      
      return {
        customer_id: customerId,
        customer_name: firstBill.customer_info?.customer_name || firstBill.customer_name,
        customer_info: firstBill.customer_info,
        pod_count: customerBills.length,
        pod_ids: customerBills.map(bill => bill.pod_id),
        billing_period: firstBill.billing_period,
        total_consumption_kwh: totalConsumption,
        total_cost_huf: totalCost,
        average_price_huf_kwh: averagePrice,
        provider: firstBill.provider, // Assuming all PODs of a customer use same provider
        bills: customerBills
      };
    }).sort((a, b) => b.total_cost_huf - a.total_cost_huf); // Sort by cost desc
  }, [filteredBills]);
  
  // Pagination calculations
  const currentData = viewMode === 'individual' ? filteredBills : consolidatedBills;
  const totalPages = Math.ceil(currentData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = currentData.slice(startIndex, endIndex);
  
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredBills.length, viewMode, itemsPerPage]);
  
  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCustomer('');
    setCustomerQuery('');
    setSelectedProvider('');
    setSelectedCustomerType('');
    setSelectedMonth('');
    setCurrentPage(1);
  };

  const getStatusColor = (status: string) => {
    const statusConfig = statusOptions.find(opt => opt.value === status);
    return statusConfig?.color || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="w-5 h-5 text-yellow-500" />;
      case 'processed':
        return <ExclamationTriangleIcon className="w-5 h-5 text-blue-500" />;
      case 'sent':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'paid':
        return <CurrencyDollarIcon className="w-5 h-5 text-gray-500" />;
      default:
        return <DocumentTextIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const handleViewDetails = (bill: EnergiaBill) => {
    setSelectedBill(bill);
  };

  const getProviderColor = (provider: string) => {
    const colors: Record<string, string> = {
      'demasz': 'bg-blue-100 text-blue-800',
      'elmu': 'bg-green-100 text-green-800',
      'emasz': 'bg-purple-100 text-purple-800',
      'eon': 'bg-orange-100 text-orange-800',
      'eon-mvm': 'bg-amber-100 text-amber-800',
      'opus': 'bg-red-100 text-red-800',
      'mvmi': 'bg-indigo-100 text-indigo-800'
    };
    return colors[provider] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <LoadingScreen />;
  }

  // Clear Admin Export Functions
  const exportForClearAdmin = (format: 'csv' | 'excel') => {
    const exportData = viewMode === 'individual' ? filteredBills : consolidatedBills;
    const currentMonth = selectedMonth || 'minden_honap';
    
    if (format === 'csv') {
      const csvData = exportData.map(item => {
        if (viewMode === 'individual') {
          const bill = item as EnergiaBill;
          return {
            'Ügyfél név': bill.customer_info?.customer_name || bill.customer_name,
            'POD ID': bill.pod_id,
            'Szolgáltató': bill.provider.toUpperCase(),
            'Számlázási időszak': bill.billing_period,
            'Fogyasztás (kWh)': bill.total_consumption_kwh,
            'Összeg (HUF)': bill.total_cost_huf,
            'Ár/kWh': bill.total_consumption_kwh > 0 ? (bill.total_cost_huf / bill.total_consumption_kwh).toFixed(2) : '0',
            'Ügyfél típus': getCustomerType(bill),
            'Adószám': bill.customer_info?.tax_number || '',
            'Cím': bill.customer_info?.address || '',
            'Email': bill.customer_info?.contact_email || '',
            'Telefon': bill.customer_info?.contact_phone || '',
            'Számlázási állapot': bill.billing_status,
            'CRM kapcsolat': bill.customer_info ? 'Igen' : 'Nem'
          };
        } else {
          const consolidatedBill = item as ConsolidatedBill;
          return {
            'Ügyfél név': consolidatedBill.customer_name,
            'POD számok': consolidatedBill.pod_ids.join('; '),
            'POD darabszám': consolidatedBill.pod_count,
            'Szolgáltató': consolidatedBill.provider.toUpperCase(),
            'Számlázási időszak': consolidatedBill.billing_period,
            'Összes fogyasztás (kWh)': consolidatedBill.total_consumption_kwh,
            'Összes összeg (HUF)': consolidatedBill.total_cost_huf,
            'Átlagár/kWh': consolidatedBill.average_price_huf_kwh.toFixed(2),
            'Adószám': consolidatedBill.customer_info?.tax_number || '',
            'Cím': consolidatedBill.customer_info?.address || '',
            'Email': consolidatedBill.customer_info?.contact_email || '',
            'Telefon': consolidatedBill.customer_info?.contact_phone || '',
            'CRM kapcsolat': consolidatedBill.customer_info ? 'Igen' : 'Nem'
          };
        }
      });

      const csvContent = [
        Object.keys(csvData[0]).join(','),
        ...csvData.map(row => Object.values(row).map(value => `"${value}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `clear_admin_export_${currentMonth}_${viewMode}_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      console.log(`Clear Admin CSV export kész: ${exportData.length} rekord, ${currentMonth} időszak`);
    }
    
    if (format === 'excel') {
      // Excel export implementáció később
      alert(`Excel export fejlesztés alatt - ${exportData.length} rekord kész exportálásra`);
    }
  };

  const generateMonthlyReport = () => {
    const reportData = {
      month: selectedMonth || 'Összes hónap',
      provider: selectedProvider || 'Minden szolgáltató', 
      totalBills: viewMode === 'individual' ? filteredBills.length : consolidatedBills.length,
      totalCustomers: uniqueCustomers.length,
      totalAmount: totalAmount,
      totalConsumption: totalConsumption,
      averagePrice: totalConsumption > 0 ? totalAmount / totalConsumption : 0,
      crmCoverage: Math.round((crmCompaniesCount / filteredBills.length) * 100),
      generatedAt: new Date().toLocaleString('hu-HU')
    };

    console.log('Havi jelentés generálása:', reportData);
    alert(`Havi jelentés generálás alatt - ${reportData.month} időszak, ${reportData.totalBills} számla`);
  };

  const exportCustomerList = () => {
    const customerData = uniqueCustomers.map(customerName => {
      const customerBills = filteredBills.filter(bill => 
        (bill.customer_info?.customer_name || bill.customer_name) === customerName
      );
      const firstBill = customerBills[0];
      
      return {
        'Ügyfél név': customerName,
        'POD számok': customerBills.map(bill => bill.pod_id).join('; '),
        'POD darabszám': customerBills.length,
        'Főszolgáltató': firstBill.provider.toUpperCase(),
        'Összes fogyasztás': customerBills.reduce((sum, bill) => sum + bill.total_consumption_kwh, 0),
        'Összes költség': customerBills.reduce((sum, bill) => sum + bill.total_cost_huf, 0),
        'Adószám': firstBill.customer_info?.tax_number || '',
        'Cím': firstBill.customer_info?.address || '',
                 'Email': firstBill.customer_info?.contact_email || '',
         'Telefon': firstBill.customer_info?.contact_phone || '',
        'Ügyfél típus': getCustomerType(firstBill),
        'CRM kapcsolat': firstBill.customer_info ? 'Igen' : 'Nem',
        'Utolsó számlázás': Math.max(...customerBills.map(bill => new Date(bill.billing_period).getTime()))
      };
    });

    const csvContent = [
      Object.keys(customerData[0]).join(','),
      ...customerData.map(row => Object.values(row).map(value => `"${value}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `clear_admin_ugyfelek_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    console.log(`Ügyféllista export kész: ${customerData.length} ügyfél`);
  };

  return (
    <div className="space-y-4 xl:space-y-6 pb-20 sm:pb-0 px-2 xl:px-0">
      {/* Header */}
      <CommonPageHeader
        title="Energia Billing Queue"
        subtitle="Számlázási sor kezelése és feldolgozása"
        icon={ReceiptPercentIcon}
        onRefresh={loadBills}
      >
        <button
          onClick={() => setShowAnalyticsModal(true)}
          className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors"
        >
          <ChartBarIcon className="w-5 h-5 mr-2" />
          Analitika
        </button>
      </CommonPageHeader>

      {/* Email Test Mode Toggle */}
      <div className="flex justify-end px-2">
        <EmailTestModeToggle />
      </div>

      {/* Enhanced Stats Cards with corrected POD vs Bill logic */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-3 lg:gap-4 xl:gap-6">
        <div className="bg-white rounded-xl p-4 lg:p-4 xl:p-6 shadow-md">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center space-x-3 lg:space-x-0 lg:flex-col lg:items-start lg:flex-1 lg:min-w-0">
              <div className="p-2 lg:p-1 xl:p-3 rounded-lg bg-blue-100 lg:hidden">
                <ReceiptPercentIcon className="w-5 h-5 text-blue-600" />
            </div>
              <div className="flex-1 lg:mb-2">
                <p className="text-gray-600 text-sm lg:text-xs xl:text-sm font-medium">
                  {viewMode === 'individual' ? 'POD-szintű számlák' : 'Konszolidált számlák'}
                </p>
                <p className="text-gray-800 text-xl lg:text-sm xl:text-base 2xl:text-2xl font-bold lg:font-semibold mt-0.5 lg:mt-1">
                  {viewMode === 'individual' ? filteredBills.length : consolidatedBills.length}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {viewMode === 'individual' ? `${uniqueCustomers.length} ügyfél POD-jai` : `${filteredBills.length} POD összevonva`}
                </p>
              </div>
            </div>
            <div className="hidden lg:block p-1 xl:p-3 rounded-lg bg-blue-100 lg:ml-2">
              <ReceiptPercentIcon className="w-4 h-4 xl:w-6 xl:h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 lg:p-4 xl:p-6 shadow-md">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center space-x-3 lg:space-x-0 lg:flex-col lg:items-start lg:flex-1 lg:min-w-0">
              <div className="p-2 lg:p-1 xl:p-3 rounded-lg bg-green-100 lg:hidden">
                <CurrencyDollarIcon className="w-5 h-5 text-green-600" />
            </div>
              <div className="flex-1 lg:mb-2">
                <p className="text-gray-600 text-sm lg:text-xs xl:text-sm font-medium">Összes érték</p>
                <p className="text-gray-800 text-xl lg:text-sm xl:text-base 2xl:text-2xl font-bold lg:font-semibold mt-0.5 lg:mt-1">
                  {(totalAmount / 1000000000).toFixed(1)}Mrd
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  HUF ({viewMode === 'individual' ? 'POD' : 'konszolidált'} szinten)
                </p>
              </div>
            </div>
            <div className="hidden lg:block p-1 xl:p-3 rounded-lg bg-green-100 lg:ml-2">
              <CurrencyDollarIcon className="w-4 h-4 xl:w-6 xl:h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 lg:p-4 xl:p-6 shadow-md">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center space-x-3 lg:space-x-0 lg:flex-col lg:items-start lg:flex-1 lg:min-w-0">
              <div className="p-2 lg:p-1 xl:p-3 rounded-lg bg-yellow-100 lg:hidden">
                <BoltIcon className="w-5 h-5 text-yellow-600" />
            </div>
              <div className="flex-1 lg:mb-2">
                <p className="text-gray-600 text-sm lg:text-xs xl:text-sm font-medium">Összes fogyasztás</p>
                <p className="text-gray-800 text-xl lg:text-sm xl:text-base 2xl:text-2xl font-bold lg:font-semibold mt-0.5 lg:mt-1">
                  {(totalConsumption / 1000000).toFixed(1)}M
                </p>
                <p className="text-xs text-gray-500 mt-1">kWh összes POD</p>
              </div>
            </div>
            <div className="hidden lg:block p-1 xl:p-3 rounded-lg bg-yellow-100 lg:ml-2">
              <BoltIcon className="w-4 h-4 xl:w-6 xl:h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 lg:p-4 xl:p-6 shadow-md">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center space-x-3 lg:space-x-0 lg:flex-col lg:items-start lg:flex-1 lg:min-w-0">
              <div className="p-2 lg:p-1 xl:p-3 rounded-lg bg-purple-100 lg:hidden">
                <BuildingOffice2Icon className="w-5 h-5 text-purple-600" />
            </div>
              <div className="flex-1 lg:mb-2">
                <p className="text-gray-600 text-sm lg:text-xs xl:text-sm font-medium">Egyedi ügyfelek</p>
                                 <p className="text-gray-800 text-xl lg:text-sm xl:text-base 2xl:text-2xl font-bold lg:font-semibold mt-0.5 lg:mt-1">
                   {uniqueCustomers.length}
                 </p>
                 <p className="text-xs text-gray-500 mt-1">{filteredBills.length} POD összesen</p>
              </div>
            </div>
            <div className="hidden lg:block p-1 xl:p-3 rounded-lg bg-purple-100 lg:ml-2">
              <BuildingOffice2Icon className="w-4 h-4 xl:w-6 xl:h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 lg:p-4 xl:p-6 shadow-md">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center space-x-3 lg:space-x-0 lg:flex-col lg:items-start lg:flex-1 lg:min-w-0">
              <div className="p-2 lg:p-1 xl:p-3 rounded-lg bg-indigo-100 lg:hidden">
                <ArrowTrendingUpIcon className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1 lg:mb-2">
                <p className="text-gray-600 text-sm lg:text-xs xl:text-sm font-medium">Átlag POD/Ügyfél</p>
                                 <p className="text-gray-800 text-xl lg:text-sm xl:text-base 2xl:text-2xl font-bold lg:font-semibold mt-0.5 lg:mt-1">
                   {uniqueCustomers.length > 0 ? (filteredBills.length / uniqueCustomers.length).toFixed(1) : '0'}
                 </p>
                 <p className="text-xs text-gray-500 mt-1">
                   {filteredBills.length > uniqueCustomers.length ? 'Multi-POD ügyfelek' : 'Egyszerű szerkezet'}
              </p>
            </div>
            </div>
            <div className="hidden lg:block p-1 xl:p-3 rounded-lg bg-indigo-100 lg:ml-2">
              <ArrowTrendingUpIcon className="w-4 h-4 xl:w-6 xl:h-6 text-indigo-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 lg:p-4 xl:p-6 shadow-md">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center space-x-3 lg:space-x-0 lg:flex-col lg:items-start lg:flex-1 lg:min-w-0">
              <div className="p-2 lg:p-1 xl:p-3 rounded-lg bg-orange-100 lg:hidden">
                <CpuChipIcon className="w-5 h-5 text-orange-600" />
              </div>
              <div className="flex-1 lg:mb-2">
                <p className="text-gray-600 text-sm lg:text-xs xl:text-sm font-medium">CRM Kapcsolat</p>
                <p className="text-gray-800 text-xl lg:text-sm xl:text-base 2xl:text-2xl font-bold lg:font-semibold mt-0.5 lg:mt-1">
                  {Math.round((crmCompaniesCount / filteredBills.length) * 100)}%
                </p>
                <p className="text-xs text-gray-500 mt-1">{crmCompaniesCount} POD CRM-ben</p>
            </div>
            </div>
            <div className="hidden lg:block p-1 xl:p-3 rounded-lg bg-orange-100 lg:ml-2">
              <CpuChipIcon className="w-4 h-4 xl:w-6 xl:h-6 text-orange-600" />
          </div>
        </div>
      </div>

        <div className="bg-white rounded-xl p-4 lg:p-4 xl:p-6 shadow-md">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center space-x-3 lg:space-x-0 lg:flex-col lg:items-start lg:flex-1 lg:min-w-0">
              <div className="p-2 lg:p-1 xl:p-3 rounded-lg bg-gray-100 lg:hidden">
                <ClockIcon className="w-5 h-5 text-gray-600" />
              </div>
              <div className="flex-1 lg:mb-2">
                <p className="text-gray-600 text-sm lg:text-xs xl:text-sm font-medium">Átlagár tartomány</p>
                                 <p className="text-gray-800 text-xl lg:text-sm xl:text-base 2xl:text-2xl font-bold lg:font-semibold mt-0.5 lg:mt-1">
                   {(() => {
                     const providerPrices = uniqueProviders.map(p => {
                       const providerBills = filteredBills.filter(bill => bill.provider === p);
                       const totalCons = providerBills.reduce((sum, bill) => sum + bill.total_consumption_kwh, 0);
                       const totalCost = providerBills.reduce((sum, bill) => sum + bill.total_cost_huf, 0);
                       return totalCons > 0 ? totalCost / totalCons : 0;
                     }).filter(price => price > 0);
                     
                     if (providerPrices.length === 0) return '0 - 0';
                     
                     const minPrice = Math.min(...providerPrices);
                     const maxPrice = Math.max(...providerPrices);
                     return `${Math.round(minPrice)} - ${Math.round(maxPrice)}`;
                   })()}
                 </p>
                <p className="text-xs text-gray-500 mt-1">Ft/kWh szolgáltatónként</p>
              </div>
            </div>
            <div className="hidden lg:block p-1 xl:p-3 rounded-lg bg-gray-100 lg:ml-2">
              <ClockIcon className="w-4 h-4 xl:w-6 xl:h-6 text-gray-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 lg:p-4 xl:p-6 shadow-md">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center space-x-3 lg:space-x-0 lg:flex-col lg:items-start lg:flex-1 lg:min-w-0">
              <div className="p-2 lg:p-1 xl:p-3 rounded-lg bg-red-100 lg:hidden">
                <ArrowPathIcon className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1 lg:mb-2">
                <p className="text-gray-600 text-sm lg:text-xs xl:text-sm font-medium">Számlázási hónapok</p>
                <p className="text-gray-800 text-xl lg:text-sm xl:text-base 2xl:text-2xl font-bold lg:font-semibold mt-0.5 lg:mt-1">
                  {uniqueMonths.length}
                </p>
                <p className="text-xs text-gray-500 mt-1">aktív számlázási periódus</p>
              </div>
            </div>
            <div className="hidden lg:block p-1 xl:p-3 rounded-lg bg-red-100 lg:ml-2">
              <ArrowPathIcon className="w-4 h-4 xl:w-6 xl:h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>



      {/* Clear Admin Export */}
      <ClearAdminDownloader className="mb-6" />

      {/* Advanced Filters */}
      <div className="bg-white rounded-xl p-4 xl:p-5 2xl:p-6 shadow-md">
        <div className="space-y-4">
          {/* Header with Clear Button */}
          <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            <div className="flex items-center space-x-2">
              <FunnelIcon className="w-5 h-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Szűrési beállítások</span>
            </div>
            <button
              onClick={clearFilters}
              className="text-sm text-red-600 hover:text-red-800 font-medium flex items-center"
            >
              <XMarkIcon className="w-4 h-4 mr-1" />
              Szűrők törlése
            </button>
          </div>

          {/* Search and Customer Dropdown */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            {/* Search */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Keresés</label>
              <input
                type="text"
                placeholder="Ügyfél neve vagy POD ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Customer Selection */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">CRM cégek ({uniqueCustomers.length})</label>
              <Combobox
                value={selectedCustomer}
                onChange={(value) => setSelectedCustomer(value)}
              >
                <div className="relative">
                  <div className="relative w-full">
                    <Combobox.Input
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2.5 sm:py-2 text-sm text-gray-800 
                              placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500
                              touch-manipulation min-h-[44px] sm:min-h-[auto]"
                      onChange={event => setCustomerQuery(event.target.value)}
                      onClick={() => {
                        // Üres keresésre minden CRM céget mutassunk
                        setCustomerQuery('');
                      }}
                      displayValue={(customerName: string) => customerName || ''}
                      placeholder="Válassz CRM céget..."
                    />
                    <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronUpDownIcon
                        className="h-5 w-5 text-gray-500"
                        aria-hidden="true"
                      />
                    </Combobox.Button>
                  </div>
                  <Combobox.Options
                    className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg 
                            ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                  >
                    {/* Minden CRM cég opció */}
                    <Combobox.Option
                      value=""
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-3 px-4 sm:py-2 ${active ? 'bg-primary-100 text-primary-900' : 'text-gray-800'
                        }`
                      }
                    >
                      {({ selected, active }) => (
                        <div className="flex items-center space-x-3">
                          <div className="p-1 rounded bg-gray-100">
                            <BuildingOffice2Icon className="h-4 w-4 text-gray-600" />
                          </div>
                          <div>
                            <div className="font-medium">
                              Minden CRM cég
                            </div>
                            <div className="text-xs text-gray-500">
                              {crmCompaniesCount} számla
                            </div>
                          </div>
                        </div>
                      )}
                    </Combobox.Option>
                    {filteredCustomers.length === 0 && customerQuery !== '' ? (
                      <div className="relative cursor-default select-none py-2 px-4 text-gray-500">
                        Nincs találat
                      </div>
                    ) : (
                      filteredCustomers.map(customer => (
                        <Combobox.Option
                          key={customer}
                          value={customer}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-3 px-4 sm:py-2 ${active ? 'bg-primary-100 text-primary-900' : 'text-gray-800'
                            }`
                          }
                        >
                          {({ selected, active }) => (
                            <div className="flex items-center space-x-3">
                              <div className="p-1 rounded bg-blue-100">
                                <BuildingOffice2Icon className="h-4 w-4 text-blue-600" />
                              </div>
                              <div>
                                <div className="font-medium">
                                  {customer}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {bills.filter(bill => 
                                    bill.customer_info?.customer_name === customer
                                  ).length} számla
                                </div>
                              </div>
                            </div>
                          )}
                        </Combobox.Option>
                      ))
                    )}
                  </Combobox.Options>
                </div>
              </Combobox>
            </div>
          </div>

          {/* Button Filters - All in one row */}
          <div className="flex flex-wrap items-start gap-4 sm:gap-6">
            {/* View Mode Filter */}
            <div className="flex-shrink-0">
              <label className="block text-xs font-medium text-gray-700 mb-2">Nézet</label>
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() => setViewMode('individual')}
                  className={`px-3 py-2 sm:py-1.5 text-sm font-medium rounded-full transition-colors touch-manipulation ${
                    viewMode === 'individual'
                      ? 'bg-blue-100 text-blue-800 border-2 border-blue-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  📊 POD szintű ({filteredBills.length})
                </button>
                <button
                  onClick={() => setViewMode('consolidated')}
                  className={`px-3 py-2 sm:py-1.5 text-sm font-medium rounded-full transition-colors touch-manipulation ${
                    viewMode === 'consolidated'
                      ? 'bg-purple-100 text-purple-800 border-2 border-purple-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  📈 Konszolidált ({consolidatedBills.length})
                </button>
              </div>
            </div>
            {/* Provider Filter Buttons */}
            <div className="flex-shrink-0">
              <label className="block text-xs font-medium text-gray-700 mb-2">Szolgáltató</label>
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() => setSelectedProvider('')}
                  className={`px-3 py-2 sm:py-1.5 text-sm font-medium rounded-full transition-colors touch-manipulation ${
                    !selectedProvider
                      ? 'bg-primary-100 text-primary-800 border-2 border-primary-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Minden
                </button>
                {uniqueProviders.map(provider => (
                  <button
                    key={provider}
                    onClick={() => setSelectedProvider(provider)}
                    className={`px-3 py-2 sm:py-1.5 text-sm font-medium rounded-full transition-colors touch-manipulation ${
                      selectedProvider === provider
                        ? 'bg-primary-100 text-primary-800 border-2 border-primary-300'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {provider.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {/* Customer Type Filter Buttons */}
            <div className="flex-shrink-0">
              <label className="block text-xs font-medium text-gray-700 mb-2">Ügyfél típus</label>
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() => setSelectedCustomerType('')}
                  className={`px-3 py-2 sm:py-1.5 text-sm font-medium rounded-full transition-colors touch-manipulation ${
                    !selectedCustomerType
                      ? 'bg-primary-100 text-primary-800 border-2 border-primary-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Minden
                </button>
                <button
                  onClick={() => setSelectedCustomerType('organization')}
                  className={`px-3 py-2 sm:py-1.5 text-sm font-medium rounded-full transition-colors touch-manipulation ${
                    selectedCustomerType === 'organization'
                      ? 'bg-purple-100 text-purple-800 border-2 border-purple-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  🏢 Cégek
                </button>
                <button
                  onClick={() => setSelectedCustomerType('individual')}
                  className={`px-3 py-2 sm:py-1.5 text-sm font-medium rounded-full transition-colors touch-manipulation ${
                    selectedCustomerType === 'individual'
                      ? 'bg-green-100 text-green-800 border-2 border-green-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  👤 Magánszemély
                </button>
                <button
                  onClick={() => setSelectedCustomerType('no_crm')}
                  className={`px-3 py-2 sm:py-1.5 text-sm font-medium rounded-full transition-colors touch-manipulation ${
                    selectedCustomerType === 'no_crm'
                      ? 'bg-orange-100 text-orange-800 border-2 border-orange-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  ⚠️ CRM nélkül
                </button>
              </div>
            </div>

            {/* Month Filter Buttons */}
            <div className="flex-shrink-0">
              <label className="block text-xs font-medium text-gray-700 mb-2">Hónap</label>
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() => setSelectedMonth('')}
                  className={`px-3 py-2 sm:py-1.5 text-sm font-medium rounded-full transition-colors touch-manipulation ${
                    !selectedMonth
                      ? 'bg-primary-100 text-primary-800 border-2 border-primary-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Minden
                </button>
                {uniqueMonths.map(month => (
                  <button
                    key={month}
                    onClick={() => setSelectedMonth(month)}
                    className={`px-3 py-2 sm:py-1.5 text-sm font-medium rounded-full transition-colors touch-manipulation ${
                      selectedMonth === month
                        ? 'bg-blue-100 text-blue-800 border-2 border-blue-300'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {month}
                  </button>
                ))}
              </div>
            </div>

            {/* Status Filter Buttons */}
            <div className="flex-shrink-0">
              <label className="block text-xs font-medium text-gray-700 mb-2">Státusz</label>
              <div className="flex flex-wrap gap-1">
                {statusOptions.map(status => (
                  <button
                    key={status.value}
                    onClick={() => setSelectedStatus(status.value)}
                    className={`px-3 py-2 sm:py-1.5 text-sm font-medium rounded-full transition-colors touch-manipulation ${
                      selectedStatus === status.value
                        ? status.color + ' border-2 border-current'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {status.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Filter Summary */}
          {(searchQuery || selectedCustomer || selectedProvider || selectedCustomerType || selectedMonth) && (
            <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200">
              <span className="text-xs text-gray-500">Aktív szűrők:</span>
              {searchQuery && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                  🔍 "{searchQuery}"
                  <button onClick={() => setSearchQuery('')} className="ml-1 hover:text-blue-600">
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedCustomer && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-indigo-100 text-indigo-800">
                  👤 {selectedCustomer}
                  <button onClick={() => setSelectedCustomer('')} className="ml-1 hover:text-indigo-600">
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedProvider && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                  ⚡ {selectedProvider.toUpperCase()}
                  <button onClick={() => setSelectedProvider('')} className="ml-1 hover:text-green-600">
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedCustomerType && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800">
                  {selectedCustomerType === 'organization' ? '🏢 Cégek' : 
                   selectedCustomerType === 'individual' ? '👤 Magánszemélyek' : '⚠️ CRM nélkül'}
                  <button onClick={() => setSelectedCustomerType('')} className="ml-1 hover:text-purple-600">
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedMonth && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-800">
                  📅 {selectedMonth}
                  <button onClick={() => setSelectedMonth('')} className="ml-1 hover:text-orange-600">
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bills Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden xl:block">
          {/* Scroll hint */}
          <div className="px-4 py-2 bg-gray-50 border-b text-xs text-gray-500 flex items-center justify-between">
            <span>📄 Számlák lista - {filteredBills.length} elem (összes: {bills.length}) - Kattints egy sorra a részletekért</span>
            <span className="flex items-center">
              <span className="mr-2">← Görgess vízszintesen →</span>
              <ArrowPathIcon className="w-3 h-3" />
            </span>
          </div>
          <div 
            className="overflow-x-auto overflow-y-visible" 
            style={{ 
              scrollbarWidth: 'thin', 
              scrollbarColor: '#3B82F6 #E5E7EB',
              maxWidth: '100%'
            }}
          >
            <table className="w-full divide-y divide-gray-200 energia-billing-table" style={{ minWidth: '1200px' }}>
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '18%', minWidth: '200px' }}>
                    Ügyfél
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '5%', minWidth: '50px' }}>
                    CRM
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '22%', minWidth: '220px' }}>
                    {viewMode === 'individual' ? 'POD ID' : 'POD-ok'}
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '8%', minWidth: '90px' }}>
                    Szolgáltató
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '10%', minWidth: '100px' }}>
                    Időszak
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '10%', minWidth: '100px' }}>
                    Fogyasztás
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '10%', minWidth: '100px' }}>
                    Összeg
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '10%', minWidth: '100px' }}>
                    Státusz
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 shadow-lg border-l border-gray-200" style={{ width: '7%', minWidth: '70px' }}>
                    Műveletek
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                      <div className="flex items-center justify-center">
                        <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                        Betöltés...
                      </div>
                    </td>
                  </tr>
                ) : filteredBills.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                      {bills.length === 0 ? 'Nincsenek számlák ebben a státuszban' : 'Nincs találat a megadott szűrőfeltételekre'}
                    </td>
                  </tr>
                ) : viewMode === 'individual' ? (
                  (currentPageData as EnergiaBill[]).map((bill) => (
                    <tr 
                      key={bill.id} 
                      className="hover:bg-blue-50 hover:shadow-sm cursor-pointer transition-all duration-200"
                      onClick={() => handleViewDetails(bill)}
                    >
                      <td className="px-3 py-3 whitespace-nowrap" style={{ minHeight: '64px' }}>
                        <div className="flex flex-col justify-center h-full">
                          <div className="text-xs lg:text-sm xl:text-base font-medium text-gray-900 truncate" title={bill.customer_info ? bill.customer_info.customer_name : "Ismeretlen ügyfél"}>
                            {bill.customer_info ? bill.customer_info.customer_name : "Ismeretlen ügyfél"}
                          </div>
                          {bill.customer_info?.customer_type && (
                            <span className={`inline-flex px-1 py-0.5 text-xs font-semibold rounded-full w-fit mt-1 ${bill.customer_info.customer_type === 'organization' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}`}>
                              {bill.customer_info.customer_type === 'organization' ? '🏢' : '👤'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap" style={{ minHeight: '48px' }}>
                        <div className="flex items-center justify-center h-full">
                          <span className={`text-xs font-medium ${bill.customer_info ? 'text-green-600' : 'text-orange-600'}`}>
                            {bill.customer_info ? '✓' : '✗'}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-xs lg:text-sm xl:text-base text-gray-900 font-mono" style={{ minHeight: '64px' }}>
                        <div className="flex items-center h-full">
                          <span className="truncate">{bill.pod_id}</span>
                        </div>
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap" style={{ minHeight: '64px' }}>
                        <div className="flex items-center h-full">
                          <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${getProviderColor(bill.provider)}`}>
                            {bill.provider.toUpperCase()}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-900" style={{ minHeight: '64px' }}>
                        <div className="flex items-center h-full">
                          {bill.billing_period}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-900" style={{ minHeight: '64px' }}>
                        <div className="flex items-center h-full">
                          {(bill.total_consumption_kwh / 1000).toFixed(0)}k kWh
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-900" style={{ minHeight: '64px' }}>
                        <div className="flex items-center h-full">
                          {(bill.total_cost_huf / 1000000).toFixed(1)}M Ft
                        </div>
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(bill.billing_status)}
                          <span className={`ml-1 inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(bill.billing_status)}`}>
                            {statusOptions.find(s => s.value === bill.billing_status)?.label || bill.billing_status}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-xs font-medium sticky right-0 bg-white shadow-lg border-l border-gray-200">
                        <div className="flex items-center justify-center text-primary-600">
                          <EyeIcon className="w-4 h-4" />
                          <span className="ml-1 hidden xl:inline">Részletek</span>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  (currentPageData as ConsolidatedBill[]).map((consolidatedBill) => (
                    <tr 
                      key={consolidatedBill.customer_id} 
                      className="hover:bg-blue-50 hover:shadow-sm cursor-pointer transition-all duration-200"
                      onClick={() => setSelectedConsolidatedBill(consolidatedBill)}
                    >
                      <td className="px-3 py-3 whitespace-nowrap" style={{ minHeight: '64px' }}>
                        <div className="flex flex-col justify-center h-full">
                          <div className="text-xs lg:text-sm xl:text-base font-medium text-gray-900 truncate" title={consolidatedBill.customer_name}>
                            {consolidatedBill.customer_name}
                          </div>
                          {consolidatedBill.customer_info?.customer_type && (
                            <span className={`inline-flex px-1 py-0.5 text-xs font-semibold rounded-full w-fit mt-1 ${consolidatedBill.customer_info.customer_type === 'organization' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}`}>
                              {consolidatedBill.customer_info.customer_type === 'organization' ? '🏢' : '👤'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap" style={{ minHeight: '48px' }}>
                        <div className="flex items-center justify-center h-full">
                          <span className={`text-xs font-medium ${consolidatedBill.customer_info ? 'text-green-600' : 'text-orange-600'}`}>
                            {consolidatedBill.customer_info ? '✓' : '✗'}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-900" style={{ minHeight: '64px' }}>
                        <div className="flex flex-col justify-center h-full">
                          <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold w-fit">
                            {consolidatedBill.pod_count} POD
                          </span>
                          <div className="text-xs text-gray-500 mt-1 truncate max-w-[180px]" title={consolidatedBill.pod_ids.join(', ')}>
                            {consolidatedBill.pod_ids.map(id => id.slice(-6)).join(', ')}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap" style={{ minHeight: '64px' }}>
                        <div className="flex items-center h-full">
                          <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${getProviderColor(consolidatedBill.provider)}`}>
                            {consolidatedBill.provider.toUpperCase()}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-900" style={{ minHeight: '64px' }}>
                        <div className="flex items-center h-full">
                          {consolidatedBill.billing_period}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-900" style={{ minHeight: '64px' }}>
                        <div className="flex items-center h-full">
                          {(consolidatedBill.total_consumption_kwh / 1000).toFixed(0)}k kWh
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-900" style={{ minHeight: '64px' }}>
                        <div className="flex items-center h-full">
                          {(consolidatedBill.total_cost_huf / 1000000).toFixed(1)}M Ft
                        </div>
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(consolidatedBill.bills[0].billing_status)}
                          <span className={`ml-1 inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(consolidatedBill.bills[0].billing_status)}`}>
                            Összesített
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-xs font-medium sticky right-0 bg-white shadow-lg border-l border-gray-200">
                        <div className="flex items-center justify-center text-primary-600">
                          <EyeIcon className="w-4 h-4" />
                          <span className="ml-1 hidden lg:inline">Részletek</span>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Card Layout */}
        <div className="xl:hidden">
          {/* Mobile hint */}
          <div className="px-4 py-2 bg-gray-50 border-b text-xs text-gray-500 text-center">
            📄 {currentPageData.length} számla (összes: {currentData.length}) - Oldal {currentPage}/{totalPages} - Kattints egy kártyára a részletekért
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <ArrowPathIcon className="w-8 h-8 animate-spin text-primary-600 mr-3" />
              <span className="text-gray-600">Számlák betöltése...</span>
            </div>
          ) : (viewMode === 'individual' ? filteredBills.length === 0 : consolidatedBills.length === 0) ? (
            <div className="text-center py-12 px-4">
              <ReceiptPercentIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">
                {bills.length === 0 ? 'Nincsenek számlák ebben a státuszban' : 'Nincs találat a megadott szűrőfeltételekre'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {viewMode === 'individual' ? (currentPageData as EnergiaBill[]).map((bill) => (
                <div 
                  key={bill.id} 
                  className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md hover:border-primary-200 cursor-pointer transition-all"
                  onClick={() => handleViewDetails(bill)}
                >
                  {/* Header sor - Ügyfél és Státusz */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-gray-900 truncate mb-1">
                        {bill.customer_info ? bill.customer_info.customer_name : "Ismeretlen ügyfél"}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${getProviderColor(bill.provider)}`}>
                          {bill.provider.toUpperCase()}
                        </span>
                        {bill.customer_info ? (
                          <CheckCircleIcon className="w-4 h-4 text-green-500" />
                        ) : (
                          <ExclamationTriangleIcon className="w-4 h-4 text-orange-500" />
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-2">
                      <div className="flex items-center">
                        {getStatusIcon(bill.billing_status)}
                        <span className={`ml-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(bill.billing_status)}`}>
                          {statusOptions.find(s => s.value === bill.billing_status)?.label || bill.billing_status}
                        </span>
                      </div>
                      <EyeIcon className="w-5 h-5 text-primary-600" />
                    </div>
                  </div>

                  {/* POD ID információ */}
                  <div className="mb-3 p-2 bg-gray-50 rounded-md">
                    <div className="text-xs text-gray-500 mb-1">POD ID:</div>
                    <div className="text-sm font-medium text-gray-900 font-mono truncate">
                      {bill.pod_id}
                    </div>
                  </div>
                  
                  {/* Fő adatok - 2 oszlop grid */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="bg-blue-50 p-2 rounded-md">
                      <div className="text-xs text-gray-500 mb-1">Fogyasztás</div>
                      <div className="text-sm font-semibold text-gray-900">
                        {(bill.total_consumption_kwh / 1000).toFixed(0)}k kWh
                      </div>
                    </div>
                    <div className="bg-green-50 p-2 rounded-md">
                      <div className="text-xs text-gray-500 mb-1">Összeg</div>
                      <div className="text-sm font-semibold text-gray-900">
                        {(bill.total_cost_huf / 1000000).toFixed(1)}M Ft
                      </div>
                    </div>
                  </div>

                  {/* Alsó sor - Számlázási időszak */}
                  <div className="flex items-center justify-between text-xs">
                    <div className="text-gray-600">
                      Időszak: {bill.billing_period}
                    </div>
                    <div className="text-gray-500">
                      ID: #{bill.id.slice(-8)}
                    </div>
                  </div>
                </div>
              )) : (currentPageData as ConsolidatedBill[]).map((consolidatedBill) => (
                <div 
                  key={consolidatedBill.customer_id} 
                  className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md hover:border-primary-200 cursor-pointer transition-all"
                  onClick={() => setSelectedConsolidatedBill(consolidatedBill)}
                >
                  {/* Header sor - Ügyfél és Státusz */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-gray-900 truncate mb-1">
                        {consolidatedBill.customer_name}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${getProviderColor(consolidatedBill.provider)}`}>
                          {consolidatedBill.provider.toUpperCase()}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                          {consolidatedBill.pod_count} POD
                        </span>
                        {consolidatedBill.customer_info ? (
                          <CheckCircleIcon className="w-4 h-4 text-green-500" />
                        ) : (
                          <ExclamationTriangleIcon className="w-4 h-4 text-orange-500" />
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-2">
                      <div className="flex items-center">
                        {getStatusIcon(consolidatedBill.bills[0].billing_status)}
                        <span className={`ml-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(consolidatedBill.bills[0].billing_status)}`}>
                          Összesített
                        </span>
                      </div>
                      <EyeIcon className="w-5 h-5 text-primary-600" />
                    </div>
                  </div>

                  {/* POD-ok információ */}
                  <div className="mb-3 p-2 bg-gray-50 rounded-md">
                    <div className="text-xs text-gray-500 mb-1">{consolidatedBill.pod_count} POD összesítve:</div>
                    <div className="text-sm font-medium text-gray-900 font-mono">
                      {consolidatedBill.pod_ids.map(id => id.slice(-6)).join(', ')}
                    </div>
                  </div>
                  
                  {/* Fő adatok - 2 oszlop grid */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="bg-blue-50 p-2 rounded-md">
                      <div className="text-xs text-gray-500 mb-1">Össz fogyasztás</div>
                      <div className="text-sm font-semibold text-gray-900">
                        {(consolidatedBill.total_consumption_kwh / 1000).toFixed(0)}k kWh
                      </div>
                    </div>
                    <div className="bg-green-50 p-2 rounded-md">
                      <div className="text-xs text-gray-500 mb-1">Össz összeg</div>
                      <div className="text-sm font-semibold text-gray-900">
                        {(consolidatedBill.total_cost_huf / 1000000).toFixed(1)}M Ft
                      </div>
                    </div>
                  </div>

                  {/* Alsó sor - Számlázási időszak */}
                  <div className="flex items-center justify-between text-xs">
                    <div className="text-gray-600">
                      Időszak: {consolidatedBill.billing_period}
                    </div>
                    <div className="text-gray-500">
                      Átlag: {(consolidatedBill.average_price_huf_kwh).toFixed(1)} Ft/kWh
                    </div>
                  </div>
                </div>
              ))}

              {/* Mobil lapozás info */}
              <div className="text-center py-4 text-sm text-gray-500">
                {viewMode === 'individual' ? (
                  <>Összesen {filteredBills.length} POD-szintű számla megjelenítve</>
                ) : (
                  <>Összesen {consolidatedBills.length} összesített számla ({filteredBills.length} POD összevonva)</>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Pagination Component */}
        {currentData.length > 0 && (
          <div className="bg-white px-4 py-3 sm:px-6 border-t border-gray-200 rounded-b-lg">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <div className="flex items-center space-x-4">
                <p className="text-sm text-gray-700">
                  Megjelenítve: <span className="font-medium">{startIndex + 1}</span> - <span className="font-medium">{Math.min(endIndex, currentData.length)}</span> / <span className="font-medium">{currentData.length}</span> számla
                </p>
                <div className="flex items-center space-x-2">
                  <label htmlFor="itemsPerPage" className="text-sm text-gray-700">Laponként:</label>
                  <select
                    id="itemsPerPage"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {/* Previous Button */}
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  ←
                </button>
                
                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                    let pageNumber;
                    if (totalPages <= 7) {
                      pageNumber = i + 1;
                    } else if (currentPage <= 4) {
                      pageNumber = i + 1;
                    } else if (currentPage >= totalPages - 3) {
                      pageNumber = totalPages - 6 + i;
                    } else {
                      pageNumber = currentPage - 3 + i;
                    }
                    
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => setCurrentPage(pageNumber)}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                          currentPage === pageNumber
                            ? 'bg-primary-600 text-white'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>
                
                {/* Next Button */}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  →
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bill Details Modal */}
      {selectedBill && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 xl:p-4 z-50">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[95vh] xl:max-h-[90vh] overflow-y-auto">
            <div className="p-4 xl:p-6">
              <div className="flex items-center justify-between mb-4 xl:mb-6">
                <h2 className="text-lg xl:text-xl font-semibold text-gray-800">
                  Számla részletek
                </h2>
                <button
                  onClick={() => setSelectedBill(null)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <XMarkIcon className="w-5 h-5 xl:w-6 xl:h-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 xl:gap-6">
                <div className="space-y-3 xl:space-y-4">
                  <div>
                    <label className="block text-xs xl:text-sm font-medium text-gray-700">Ügyfél neve</label>
                    <p className="mt-1 text-xs xl:text-sm text-gray-900">
                      {selectedBill.customer_info ? selectedBill.customer_info.customer_name : "Ismeretlen ügyfél"}
                    </p>
                    {selectedBill.customer_info ? (
                      <div className="flex items-center mt-1">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          ✓ CRM kapcsolat létezik
                        </span>
                        <span className="ml-2 text-xs text-gray-500">
                          {selectedBill.customer_info.customer_type === 'organization' ? 'Szervezet' : 'Magánszemély'}
                        </span>
                      </div>
                    ) : (
                      <div className="mt-1">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                          ⚠ Nincs CRM kapcsolat
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          Ez a POD még nincs hozzárendelve egyetlen CRM ügyfélhez sem
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs xl:text-sm font-medium text-gray-700">POD ID</label>
                    <p className="mt-1 text-xs xl:text-sm text-gray-900 font-mono break-all">{selectedBill.pod_id}</p>
                  </div>

                  <div>
                    <label className="block text-xs xl:text-sm font-medium text-gray-700">Szolgáltató</label>
                    <span className={`mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getProviderColor(selectedBill.provider)}`}>
                      {selectedBill.provider.toUpperCase()}
                    </span>
                  </div>

                  <div>
                    <label className="block text-xs xl:text-sm font-medium text-gray-700">Számlázási időszak</label>
                    <p className="mt-1 text-xs xl:text-sm text-gray-900">{selectedBill.billing_period}</p>
                  </div>

                  <div>
                    <label className="block text-xs xl:text-sm font-medium text-gray-700">Státusz</label>
                    <div className="mt-1 flex items-center">
                      {getStatusIcon(selectedBill.billing_status)}
                      <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedBill.billing_status)}`}>
                        {statusOptions.find(s => s.value === selectedBill.billing_status)?.label || selectedBill.billing_status}
                      </span>
                    </div>
                  </div>

                  {/* Ügyfél kapcsolattartási adatok */}
                  {selectedBill.customer_info && (
                    <>
                      {selectedBill.customer_info.contact_email && (
                        <div>
                          <label className="block text-xs xl:text-sm font-medium text-gray-700">Email</label>
                          <p className="mt-1 text-xs xl:text-sm text-gray-900 break-all">{selectedBill.customer_info.contact_email}</p>
                        </div>
                      )}

                      {selectedBill.customer_info.contact_phone && (
                        <div>
                          <label className="block text-xs xl:text-sm font-medium text-gray-700">Telefon</label>
                          <p className="mt-1 text-xs xl:text-sm text-gray-900">{selectedBill.customer_info.contact_phone}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="space-y-3 xl:space-y-4">
                  <div>
                    <label className="block text-xs xl:text-sm font-medium text-gray-700">Fogyasztás</label>
                    <p className="mt-1 text-xs xl:text-sm text-gray-900">
                      {energiaService.formatKWH(selectedBill.total_consumption_kwh)}
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs xl:text-sm font-medium text-gray-700">Összeg</label>
                    <p className="mt-1 text-base xl:text-lg font-semibold text-gray-900">
                      {energiaService.formatHUF(selectedBill.total_cost_huf)}
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs xl:text-sm font-medium text-gray-700">Átlagos ár</label>
                    <p className="mt-1 text-xs xl:text-sm text-gray-900">
                      {selectedBill.total_consumption_kwh > 0 ? 
                        energiaService.formatHUF(selectedBill.total_cost_huf / selectedBill.total_consumption_kwh) + ' / kWh' :
                        'N/A'
                      }
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs xl:text-sm font-medium text-gray-700">Feltöltés dátuma</label>
                    <p className="mt-1 text-xs xl:text-sm text-gray-900">
                      {selectedBill.upload_timestamp?.toDate ? 
                        format(selectedBill.upload_timestamp.toDate(), 'yyyy.MM.dd HH:mm:ss', { locale: hu }) :
                        'N/A'
                      }
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs xl:text-sm font-medium text-gray-700">Feldolgozta</label>
                    <p className="mt-1 text-xs xl:text-sm text-gray-900">{selectedBill.processed_by}</p>
                  </div>

                  {/* További ügyfél adatok */}
                  {selectedBill.customer_info && (
                    <>
                      {selectedBill.customer_info.address && (
                        <div>
                          <label className="block text-xs xl:text-sm font-medium text-gray-700">Cím</label>
                          <p className="mt-1 text-xs xl:text-sm text-gray-900">
                            {typeof selectedBill.customer_info.address === 'object'
                              ? `${(selectedBill.customer_info.address as any).postalCode || ''} ${(selectedBill.customer_info.address as any).city || ''}, ${(selectedBill.customer_info.address as any).street || ''}`
                              : selectedBill.customer_info.address}
                          </p>
                        </div>
                      )}

                      {selectedBill.customer_info.tax_number && (
                        <div>
                          <label className="block text-xs xl:text-sm font-medium text-gray-700">Adószám</label>
                          <p className="mt-1 text-xs xl:text-sm text-gray-900">{selectedBill.customer_info.tax_number}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="mt-4 xl:mt-6 flex flex-col xl:flex-row justify-end space-y-3 xl:space-y-0 xl:space-x-3">
                <button
                  onClick={() => setSelectedBill(null)}
                  className="w-full xl:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors text-sm"
                >
                  Bezárás
                </button>
                <button
                  className="w-full xl:w-auto px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
                >
                  Számla letöltése
                </button>
                {selectedBill.customer_info ? (
                  <button
                    className="w-full xl:w-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    CRM ügyfél megtekintése
                  </button>
                ) : (
                  <div className="text-xs text-gray-500 flex items-center justify-center xl:justify-start">
                    <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                    CRM ügyfél megtekintése nem elérhető
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Consolidated Bill Details Modal */}
      {selectedConsolidatedBill && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 xl:p-4 z-50">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[95vh] xl:max-h-[90vh] overflow-y-auto">
            <div className="p-4 xl:p-6">
              <div className="flex items-center justify-between mb-4 xl:mb-6">
                <h2 className="text-lg xl:text-xl font-semibold text-gray-800">
                  <span className="hidden sm:inline">Összesített számla részletek</span>
                  <span className="sm:hidden">Összesített számla</span>
                </h2>
                <button
                  onClick={() => setSelectedConsolidatedBill(null)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <XMarkIcon className="w-5 h-5 xl:w-6 xl:h-6" />
                </button>
              </div>

              {/* Ügyfél összesített adatok - mobil optimalizált */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 xl:gap-6 mb-6">
                <div className="space-y-3 xl:space-y-4">
                  <div>
                    <label className="block text-xs xl:text-sm font-medium text-gray-700">Ügyfél neve</label>
                    <p className="mt-1 text-sm xl:text-base text-gray-900 font-medium">
                      {selectedConsolidatedBill.customer_name}
                    </p>
                    {selectedConsolidatedBill.customer_info ? (
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          ✓ CRM kapcsolat
                        </span>
                        <span className="text-xs text-gray-500">
                          {selectedConsolidatedBill.customer_info.customer_type === 'organization' ? 'Szervezet' : 'Magánszemély'}
                        </span>
                      </div>
                    ) : (
                      <div className="mt-2">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                          ⚠ Nincs CRM kapcsolat
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500">POD-ok száma</label>
                      <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                        {selectedConsolidatedBill.pod_count} POD
                      </span>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500">Szolgáltató</label>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getProviderColor(selectedConsolidatedBill.provider)}`}>
                        {selectedConsolidatedBill.provider.toUpperCase()}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-500">Számlázási időszak</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedConsolidatedBill.billing_period}</p>
                  </div>
                </div>

                <div className="space-y-3 xl:space-y-4">
                  {/* Mobil optimalizált összegek grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Összes fogyasztás</label>
                      <p className="text-lg font-semibold text-blue-900">
                        {(selectedConsolidatedBill.total_consumption_kwh / 1000).toFixed(0)}k kWh
                      </p>
                    </div>

                    <div className="bg-green-50 p-3 rounded-lg">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Összes összeg</label>
                      <p className="text-lg font-bold text-green-900">
                        {(selectedConsolidatedBill.total_cost_huf / 1000000).toFixed(1)}M Ft
                      </p>
                    </div>

                    <div className="bg-yellow-50 p-3 rounded-lg sm:col-span-2 lg:col-span-1">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Átlagos ár</label>
                      <p className="text-sm font-medium text-yellow-900">
                        {(selectedConsolidatedBill.average_price_huf_kwh).toFixed(1)} Ft/kWh
                      </p>
                    </div>
                  </div>

                  {/* Ügyfél kapcsolattartási adatok - mobil optimalizált */}
                  {selectedConsolidatedBill.customer_info && (
                    <div className="space-y-2">
                      {selectedConsolidatedBill.customer_info.contact_email && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Email</label>
                          <p className="text-sm text-gray-900 break-all">{selectedConsolidatedBill.customer_info.contact_email}</p>
                        </div>
                      )}

                      {selectedConsolidatedBill.customer_info.contact_phone && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Telefon</label>
                          <p className="text-sm text-gray-900">{selectedConsolidatedBill.customer_info.contact_phone}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* POD-ok részletező listája - mobil optimalizált */}
              <div className="border-t pt-6">
                <h3 className="text-base font-semibold text-gray-800 mb-4">
                  POD-ok részletesen ({selectedConsolidatedBill.pod_count} db)
                </h3>
                <div className="space-y-3">
                  {selectedConsolidatedBill.bills.map((bill, index) => (
                    <div key={bill.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      {/* Mobil: egy oszlopban, desktop: grid */}
                      <div className="space-y-3 lg:space-y-0 lg:grid lg:grid-cols-4 lg:gap-3">
                        <div className="lg:col-span-2">
                          <label className="block text-xs font-medium text-gray-500">POD #{index + 1}</label>
                          <p className="text-sm font-mono text-gray-900 break-all lg:truncate" title={bill.pod_id}>
                            {bill.pod_id}
                          </p>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3 lg:col-span-2 lg:grid-cols-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-500">Fogyasztás</label>
                            <p className="text-sm font-medium text-gray-900">
                              {(bill.total_consumption_kwh / 1000).toFixed(0)}k kWh
                            </p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500">Összeg</label>
                            <p className="text-sm font-medium text-gray-900">
                              {(bill.total_cost_huf / 1000000).toFixed(1)}M Ft
                            </p>
                          </div>
                          <div className="col-span-2 lg:col-span-1">
                            <label className="block text-xs font-medium text-gray-500">Státusz</label>
                            <div className="flex items-center">
                              {getStatusIcon(bill.billing_status)}
                              <span className={`ml-1 inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(bill.billing_status)}`}>
                                {statusOptions.find(s => s.value === bill.billing_status)?.label || bill.billing_status}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mobil optimalizált gombok */}
              <div className="mt-6 flex flex-col space-y-3 lg:flex-row lg:justify-end lg:space-y-0 lg:space-x-3">
                <button
                  onClick={() => setSelectedConsolidatedBill(null)}
                  className="w-full lg:w-auto px-4 py-3 lg:py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors text-sm font-medium"
                >
                  Bezárás
                </button>
                <button
                  className="w-full lg:w-auto px-4 py-3 lg:py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                >
                  📄 Összesített számla generálása
                </button>
                {selectedConsolidatedBill.customer_info && (
                  <button
                    className="w-full lg:w-auto px-4 py-3 lg:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                  >
                    👤 CRM ügyfél megtekintése
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Analytics Modal */}
      {showAnalyticsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-6 border-b border-gray-200 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-800">Számlázási analitika</h2>
                <button
                  onClick={() => setShowAnalyticsModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-2"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Provider Performance */}
              <div className="border rounded-lg p-4 bg-blue-50/30">
                <h4 className="text-lg font-medium text-gray-800 mb-4">Szolgáltatói teljesítmény</h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {uniqueProviders.map(provider => {
                    const providerBills = filteredBills.filter(bill => bill.provider === provider);
                    const providerConsumption = providerBills.reduce((sum, bill) => sum + bill.total_consumption_kwh, 0);
                    const providerCost = providerBills.reduce((sum, bill) => sum + bill.total_cost_huf, 0);
                    const avgPrice = providerConsumption > 0 ? providerCost / providerConsumption : 0;
                    const percentage = (providerBills.length / filteredBills.length) * 100;
                    
                    return (
                      <div key={provider} className="bg-white p-4 rounded-lg shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                          <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getProviderColor(provider)}`}>
                            {provider.toUpperCase()}
                          </span>
                          <span className="text-lg font-bold text-gray-800">{percentage.toFixed(1)}%</span>
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-center p-2 bg-gray-50 rounded">
                            <div className="text-gray-500 text-xs">Számlák</div>
                            <div className="font-bold text-lg">{providerBills.length}</div>
                          </div>
                          <div className="text-center p-2 bg-gray-50 rounded">
                            <div className="text-gray-500 text-xs">kWh</div>
                            <div className="font-bold text-lg">{(providerConsumption / 1000).toFixed(0)}k</div>
                          </div>
                          <div className="text-center p-2 bg-gray-50 rounded">
                            <div className="text-gray-500 text-xs">Ft</div>
                            <div className="font-bold text-lg">{(providerCost / 1000000).toFixed(1)}M</div>
                          </div>
                          <div className="text-center p-2 bg-gray-50 rounded">
                            <div className="text-gray-500 text-xs">Ár/kWh</div>
                            <div className="font-bold text-lg">{avgPrice.toFixed(1)}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Billing Status */}
              <div className="border rounded-lg p-4 bg-green-50/30">
                <h4 className="text-lg font-medium text-gray-800 mb-4">Számlázási állapot</h4>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {statusOptions.map(statusOption => {
                    const statusBills = bills.filter(bill => bill.billing_status === statusOption.value);
                    const statusConsumption = statusBills.reduce((sum, bill) => sum + bill.total_consumption_kwh, 0);
                    const statusCost = statusBills.reduce((sum, bill) => sum + bill.total_cost_huf, 0);
                    const percentage = bills.length > 0 ? (statusBills.length / bills.length) * 100 : 0;
                    
                    return (
                      <div key={statusOption.value} className="bg-white p-4 rounded-lg text-center shadow-sm">
                        <div className="mb-2 flex justify-center">{getStatusIcon(statusOption.value)}</div>
                        <div className="text-sm font-medium text-gray-800 mb-2">{statusOption.label}</div>
                        <div className="text-2xl font-bold text-gray-700">{statusBills.length}</div>
                        <div className="text-sm text-gray-500 mb-3">{percentage.toFixed(1)}%</div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                          <div 
                            className={`h-2 rounded-full ${
                              statusOption.value === 'pending' ? 'bg-yellow-500' :
                              statusOption.value === 'processed' ? 'bg-blue-500' :
                              statusOption.value === 'sent' ? 'bg-green-500' :
                              'bg-gray-500'
                            }`}
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <div className="text-sm text-gray-500">
                          {(statusCost / 1000000).toFixed(1)}M Ft
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Monthly Timeline */}
              <div className="border rounded-lg p-4 bg-purple-50/30">
                <h4 className="text-lg font-medium text-gray-800 mb-4">Havi számlázási idősor</h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                  {uniqueMonths.slice(0, 12).map(month => {
                    const monthBills = filteredBills.filter(bill => bill.billing_period === month);
                    const monthConsumption = monthBills.reduce((sum, bill) => sum + bill.total_consumption_kwh, 0);
                    const monthCost = monthBills.reduce((sum, bill) => sum + bill.total_cost_huf, 0);
                    const maxMonthBills = Math.max(...uniqueMonths.map(m => 
                      filteredBills.filter(bill => bill.billing_period === m).length
                    ));
                    const percentage = maxMonthBills > 0 ? (monthBills.length / maxMonthBills) * 100 : 0;
                    
                    return (
                      <div key={month} className="bg-white p-4 rounded-lg shadow-sm">
                        <div className="text-lg font-bold text-gray-800 mb-2">{month}</div>
                        <div className="text-2xl font-bold text-gray-700 mb-1">{monthBills.length}</div>
                        <div className="text-sm text-gray-500 mb-3">számla</div>
                        <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
                          <div 
                            className="h-3 rounded-full bg-gradient-to-r from-blue-500 to-green-500"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="text-center p-2 bg-gray-50 rounded">
                            <div className="text-gray-500 text-xs">kWh</div>
                            <div className="font-bold">{(monthConsumption / 1000).toFixed(0)}k</div>
                          </div>
                          <div className="text-center p-2 bg-gray-50 rounded">
                            <div className="text-gray-500 text-xs">Ft</div>
                            <div className="font-bold">{(monthCost / 1000000).toFixed(1)}M</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnergiaBilling; 